import { Target, Eye, ShieldCheck, CheckCircle2 } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { company, timeline } from "@/data/content";
import { useSiteContent } from "@/i18n/SiteContentContext";

export const About = () => {
  const { get } = useSiteContent();
  const comp = get("company", company);
  return (
  <section id="about" data-testid="about-section" className="honeycomb-bg relative overflow-hidden bg-gradient-to-b from-white via-ice/30 to-white py-28 md:py-40">
    <div className="absolute right-0 top-24 h-72 w-72 rounded-full bg-leaf/10 blur-3xl" />
    <div className="absolute -left-32 bottom-24 h-80 w-80 rounded-full bg-gold/10 blur-3xl" />
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <SectionHeading
        number="01"
        eyebrow="Our Story"
        title={<>Two decades on the road, <span className="text-leaf">rebuilt for tomorrow.</span></>}
        description={comp.intro}
      />

      <div className="relative">
        <div className="absolute left-5 top-0 h-full w-px bg-gradient-to-b from-leaf via-royal/20 to-gold md:left-1/2" />
        <div className="space-y-16">
          {timeline.map((t, i) => (
            <Reveal key={t.year} delay={i * 0.08}>
              <div className={`relative flex flex-col gap-6 pl-16 md:w-1/2 md:pl-0 ${i % 2 === 0 ? "md:pr-16 md:text-right" : "md:ml-auto md:pl-16"}`}>
                <span className={`absolute left-0 top-1 grid h-11 w-11 place-items-center rounded-full border-2 border-leaf bg-navy font-jakarta text-[10px] font-extrabold text-gold shadow-lg shadow-leaf/20 md:left-auto ${i % 2 === 0 ? "md:-right-[22px]" : "md:-left-[22px]"}`}>
                  {t.year.slice(0, 4)}
                </span>
                <div className="hex-card border border-royal/20 bg-gradient-to-br from-navy to-royal p-8 shadow-xl shadow-navy/20 transition-all duration-300 hover:-translate-y-1 hover:border-gold/40 hover:shadow-2xl hover:shadow-gold/15">
                  <p className="font-jakarta text-sm font-bold uppercase tracking-[0.25em] text-leaf">{t.year}</p>
                  <h3 className="mt-2 font-jakarta text-xl font-bold text-white md:text-2xl">{t.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/65 md:text-base">{t.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <div className="mt-28 grid gap-8 md:grid-cols-3">
        {[
          { icon: Target, title: "Mission", text: comp.mission },
          { icon: Eye, title: "Vision", text: comp.vision },
          { icon: ShieldCheck, title: "Core Values", text: company.values.join(" · ") },
        ].map((c, i) => (
          <Reveal key={c.title} delay={i * 0.1}>
            <div data-testid={`about-${c.title.toLowerCase().replace(" ", "-")}`} className="hex-card group h-full border border-leaf/25 bg-gradient-to-b from-white to-leaf/5 p-8 shadow-xl shadow-royal/10 transition-all duration-300 hover:-translate-y-1 hover:border-gold/50 hover:shadow-2xl hover:shadow-gold/15">
              <c.icon className="h-8 w-8 text-leaf transition-colors duration-300 group-hover:text-gold" />
              <h3 className="mt-4 font-jakarta text-lg font-bold text-navy">{c.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-royal/70">{c.text}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-16">
        <div className="hex-card border border-royal/15 bg-gradient-to-br from-ice/80 to-white p-8 shadow-xl shadow-royal/10 md:p-10">
          <h3 className="font-jakarta text-xl font-extrabold text-navy md:text-2xl">Why clients choose Mandair</h3>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {company.whyChooseUs.map((point) => (
              <div key={point} data-testid={`why-${point.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex items-center gap-3 rounded-xl bg-white/80 px-4 py-3 shadow-sm">
                <CheckCircle2 className="h-5 w-5 shrink-0 text-leaf" />
                <span className="text-sm font-semibold text-royal">{point}</span>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </div>
  </section>
  );
};
